import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight, Search, Store, X } from 'lucide-react'
import { Badge, EmptyState, Input, Spinner } from '@/components/ui'
import { CompanyAvatar } from '@/components/CompanyAvatar'
import { useCompanies, useOffers } from '@/hooks/useOffers'
import { useCurrentBooklet } from '@/hooks/useCurrentBooklet'
import { CATEGORY_LABELS, type Category } from '@shared/model'
import { formatCount, normalizeForSearch } from '@/lib/format'
import { cn } from '@/lib/cn'
import { QueryError } from '@/components/QueryError'

/**
 * Företagslistan – för den som vet vart hen ska gå och vill kolla om
 * stället är med i häftet, i stället för att bläddra bland erbjudanden.
 */
export function CompaniesPage() {
  const { booklet, lastExpired, loading: bookletLoading, error: bookletError } = useCurrentBooklet()
  const { data: companies, loading: companiesLoading, error: companiesError } = useCompanies()
  const { data: offers } = useOffers(booklet?.id ?? null)

  const [search, setSearch] = useState('')
  const [category, setCategory] = useState<Category | 'all'>('all')

  /** Bara företag som faktiskt har ett erbjudande i den aktuella perioden. */
  const offerCountByCompany = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const offer of offers) {
      counts[offer.companyId] = (counts[offer.companyId] ?? 0) + 1
    }
    return counts
  }, [offers])

  const participating = useMemo(
    () => companies.filter((company) => (offerCountByCompany[company.id] ?? 0) > 0),
    [companies, offerCountByCompany],
  )

  const availableCategories = useMemo(() => {
    const found = new Set<Category>()
    for (const company of participating) found.add(company.category)
    return [...found].sort((a, b) => CATEGORY_LABELS[a].localeCompare(CATEGORY_LABELS[b], 'sv'))
  }, [participating])

  const visible = useMemo(() => {
    const needle = normalizeForSearch(search)
    return participating
      .filter((company) => {
        if (category !== 'all' && company.category !== category) return false
        if (!needle) return true
        return normalizeForSearch(`${company.name} ${company.description ?? ''}`).includes(needle)
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'sv'))
  }, [participating, search, category])

  if (bookletLoading || companiesLoading) return <Spinner label="Hämtar företagen…" />
  if (bookletError || companiesError) return <QueryError error={bookletError ?? companiesError} />

  if (!booklet) {
    return (
      <EmptyState
        icon={<Store size={48} aria-hidden />}
        title={lastExpired ? 'Perioden är slut' : 'Inget häfte aktiverat'}
        description={
          lastExpired
            ? `Företagen som var med i ${lastExpired.periodName} visas inte längre. De kommer tillbaka när nästa häfte öppnar.`
            : 'Aktivera din kod först, så ser du vilka företag som är med i häftet.'
        }
      />
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-extrabold">Företag</h1>
        <p className="text-sm text-muted">
          {formatCount(participating.length, 'företag är', 'företag är')} med i {booklet.periodName}.
        </p>
      </div>

      <div className="relative">
        <Search
          size={18}
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-faint"
        />
        <Input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Sök företag"
          aria-label="Sök företag"
          className="pl-11"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            aria-label="Rensa sökningen"
            className="tap-safe absolute top-1/2 right-2 -translate-y-1/2 rounded-pill p-2 text-muted hover:text-ink"
          >
            <X size={16} aria-hidden />
          </button>
        )}
      </div>

      {availableCategories.length > 1 && (
        <div className="-mx-4 overflow-x-auto px-4 no-scrollbar">
          <div className="flex w-max gap-2 pb-1">
            {(['all', ...availableCategories] as const).map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setCategory(item as Category | 'all')}
                aria-pressed={category === item}
                className={cn(
                  'rounded-pill border px-3.5 py-2 text-sm font-semibold whitespace-nowrap transition-colors',
                  category === item
                    ? 'border-brand bg-brand text-on-brand'
                    : 'border-border bg-surface text-muted hover:text-ink',
                )}
              >
                {item === 'all' ? 'Alla' : CATEGORY_LABELS[item as Category]}
              </button>
            ))}
          </div>
        </div>
      )}

      {visible.length === 0 ? (
        <EmptyState icon={<Search size={44} aria-hidden />} title="Inga träffar" />
      ) : (
        <ul className="space-y-2.5">
          {visible.map((company) => (
            <li key={company.id}>
              <Link
                to={`/foretag/${company.id}`}
                className="flex items-center gap-3 rounded-card border border-border bg-surface p-3 shadow-card transition-colors hover:border-border-strong"
              >
                <CompanyAvatar name={company.name} logoUrl={company.logoUrl} size={52} />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display font-extrabold text-ink">{company.name}</p>
                  <p className="truncate text-sm text-muted">
                    {CATEGORY_LABELS[company.category]}
                  </p>
                </div>
                <Badge tone="brand">
                  {formatCount(offerCountByCompany[company.id] ?? 0, 'erbjudande', 'erbjudanden')}
                </Badge>
                <ChevronRight size={18} className="shrink-0 text-faint" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
